<template>
  <v-dialog
    hide-overlay
    :overlay-opacity="0"
    transition="fade-transition"
    persistent
    ref="dialog"
    v-model="isOpen"
    absolute
  >
    <div class="card-overlay" @click="$refs.dialog.animateClick()"/>

    <v-card style="width: 600px" class="" v-if="isOpen">
      <v-card-text height="800px" class="px-8 pt-8">
        <div class="mb-8 d-flex align-start">
          <div class="d-flex flex-column">
            <span class="font-weight-light p-1 base--text">
              {{ $t('heading.onboarding.import.url.title') }}
            </span>
            <h4 class>{{ $t('heading.onboarding.import.url.subtitle') }}</h4>
          </div>

          <v-btn x-small icon @click="$emit('remove-modal')" class="ml-auto">
            <v-icon size="24" color="gray">$close</v-icon>
          </v-btn>
        </div>

        <v-form ref="form">
          <v-expansion-panels v-model="panelOpen">
            <!-- SIMPLE METHOD: WP admin login details -->
            <v-expansion-panel elevation="0">
              <v-expansion-panel-header class="h6 text--heading font-weight-bold">
                {{ $t('heading.onboarding.simpleImportMethod.adminLoginDetails.title') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="mb-4 body-2 gray--text text--darken-1">
                  <p>{{ $t('heading.onboarding.simpleImportMethod.adminLoginDetails.description') }}</p>
                  <p>
                    <span class="font-weight-600 me-1">{{
                        $t('heading.onboarding.simpleImportMethod.adminLoginDetails.please_note')
                      }}:</span>
                    <span>{{ $t('heading.onboarding.simpleImportMethod.adminLoginDetails.note') }}</span>
                  </p>
                </div>

                <div v-if="panelOpen === 0">
                  <v-text-field
                    class="mt-2"
                    outlined
                    :label="$t('form.label.url')"
                    placeholder="https://example.com/wp-login.php"
                    v-model="url"
                    hide-details=""
                    validate-on-blur
                    :rules="urlRules"
                  />
                  <v-text-field
                    class="mt-4"
                    outlined
                    :label="$t('form.label.username')"
                    placeholder="admin"
                    v-model="username"
                    hide-details=""
                    validate-on-blur
                    :rules="usernameRules"
                  />
                  <v-text-field
                    class="mt-4"
                    outlined
                    :label="$t('form.label.password')"
                    type="password"
                    v-model="password"
                    hide-details=""
                    validate-on-blur
                    :rules="passwordRules"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- ADVANCED METHOD: plugin/manual -->
            <v-expansion-panel>
              <v-expansion-panel-header class="h6 text--heading font-weight-bold">
                {{ $t('heading.onboarding.simpleImportMethod.installPluginManually.title') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="panelOpen === 1" class="body-2 gray--text text--darken-1 mb-4">
                  <ol>
                    <li>
                      <p class="mb-0">{{ $t('heading.onboarding.simpleImportMethod.installPluginManually.downloadPluginHeader') }}</p>
                      <v-btn
                        class="my-1"
                        color="primary"
                        small
                        :href="downloadPluginLink"
                      >{{ $t('heading.onboarding.simpleImportMethod.installPluginManually.downloadPluginButton') }}</v-btn>
                    </li>
                    <li>{{ $t('heading.onboarding.simpleImportMethod.installPluginManually.installPlugin') }}</li>
                    <li>{{ $t('heading.onboarding.simpleImportMethod.installPluginManually.runMigration') }}</li>
                  </ol>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-form>
        <v-scale-transition>
          <div class="error-box mt-5" v-if="formError && !connectionTestLoading">
            <v-icon color="error" size="20" class="mr-2">$alertcircle</v-icon>
            <span class="error--text">
              <b class="error--text">
                <i18next :translation="$t('message.alert.failed')">
                  <template #message>
                    <span class="font-weight-medium">{{ formError }}</span>
                  </template>
                </i18next>
              </b>
            </span>
          </div>
          <div class="warning-box mt-5" v-if="formWarning && !connectionTestLoading">
            <v-icon color="warning" size="20" class="mr-2">$alertcircle</v-icon>
            <span class="warning--text">
              <b class="warning--text">
                <i18next :translation="$t('message.alert.warning')">
                  <template #message>
                    <span class="font-weight-medium">{{ formWarning }}</span>
                  </template>
                </i18next>
              </b>
            </span>
          </div>
        </v-scale-transition>
      </v-card-text>

      <v-card-actions
        class="pt-8 d-flex align-center justify-space-between"
        :class="$vuetify.breakpoint.width < 550 ? 'flex-column-reverse' : ''"
      >
        <div :class="$vuetify.breakpoint.width < 550 ? 'mt-4 w-100' : ''">
          <step-indicator :currentStep="maxSteps" :maxSteps="maxSteps"/>
        </div>
        <div
          class="d-flex align-center"
          :class="
            $vuetify.breakpoint.width < 550 ? 'flex-column-reverse w-100' : ''
          "
        >
          <v-btn
            large
            @click="$emit('remove-modal')"
            text
            :block="$vuetify.breakpoint.width < 550 ? true : false"
            elevation="0"
            class="mr-3"
          >
            <span class="font-weight-bold">
             {{ $t('button.cancel') }}
            </span>
          </v-btn>
          <v-btn
            large
            @click="handleContinueButtonClick()"
            color="primary"
            :block="$vuetify.breakpoint.width < 550 ? true : false"
            elevation="0"
            :loading="loading || connectionTestLoading || preparingImport"
          >
            <span class="font-weight-bold">
              {{ $t('button.continue') }}
            </span>
            <v-icon class="ml-2 mr-0" size="20">$arrowright</v-icon>

          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import StepIndicator from "../StepIndicator.vue";
import Api from "@/apis/Api";
import FormRulesMixin from "@/mixins/FormRulesMixin";

export default {
  name: "OnboardingImportSimpleMethodModal",
  components: {
    StepIndicator,
  },
  mixins: [FormRulesMixin],
  props: {
    isOpen: Boolean,
    loading: Boolean,
    preparingImport: Boolean,
    maxSteps: Number,
    importInstanceData: Object,
    selectedService: {
      type: Object,
      required: true
    },
  },
  data: () => ({
    panelOpen: 0,
    connectionTestLoading: false,
    url: '',
    username: '',
    password: '',
    formError: '',
    formWarning: '',
  }),
  computed: {
    downloadPluginLink() {
      return Api.defaults.baseURL + '/instances/import/download-plugin';
    },
  },
  methods: {
    async handleContinueButtonClick() {
      if (this.panelOpen === 1) {

        this.$emit("import-instance", this.importInstanceData);
        return;
      }

      this.formError = "";
      this.formWarning = "";

      try {
        if (
          !this.$refs.form.validate()
          && (this.url === "" || this.username === "" || this.password === "")
        ) {
          throw this.$t('notification.emptyFields');
        }

        this.connectionTestLoading = true;

        let result;
        if (this.isDemo) {
          result = await this.mockConnectionTest();
        } else {
          result = await this.runConnectionTest();
        }

        if (result.severity === 'error') {
          this.formError = result.message;
          return;
        }

        if (result.severity === 'warning') {
          this.formWarning = result.message;
          return;
        }

        if (result.success == true) {
          this.$emit("import-instance", result.import_details);
          return;
        }

        throw this.$t('notification.connectionFailed');
      } catch (error) {
        this.formError = error;
      } finally {
        this.connectionTestLoading = false;
      }
    },
    async runConnectionTest() {
      return new Promise((resolve, reject) => {
        let params = {
          type: 'plugin',
          service_id: this.selectedService.id,
          url: this.importInstanceData.url,
          login_url: this.url,
          username: this.username,
          password: this.password,
        };

        Api.post(`/instances/import/test-wordpress-connection`, params)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(Api.getErrorMessage(error));
          });
      });
    },
    async mockConnectionTest() {
      return new Promise((resolve, reject) => {
        if (this.hostname !== "reject") {
          setTimeout(() => {
            resolve({
              success: true,
            });
          }, 2000);
        } else {
          reject(false);
        }
      });
    },
    resetData() {
      this.panelOpen = 0;

      this.login_url = "";
      this.username = "";
      this.password = "";

      this.formError = "";
      this.formWarning = "";

      this.connectionTestLoading = false;
    },
  },
  watch: {
    isOpen: function (value) {
      this.resetData();

      if (value) {
        this.$nextTick(() => {
          this.$store.dispatch("lockBodyScroll");
        });
      } else {
        this.$store.dispatch("unlockBodyScroll");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.v-dialog__content::v-deep {
  .v-dialog {
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.v-card__text {
  -webkit-line-clamp: unset !important;
}

/* Stylizacja paneli jak w SetYourDomainModal */
.v-expansion-panel {
  border: 1px solid var(--v-gray-lighten2);

  &::before {
    display: none;
  }

  &::after {
    display: none;
  }

  &--active {
    border-color: var(--v-primary-base);
  }

  .v-expansion-panel-content::v-deep {
    .v-expansion-panel-content__wrap {
      padding-bottom: 0px;
      color: var(--v-gray-lighten1);
    }
  }

  .v-expansion-panel-header {
    padding-left: 60px;
    position: relative;

    &::before,
    &::after {
      content: "";
      position: absolute;
      left: 24px;
      top: 50%;
      transform: translateY(-50%);
      opacity: 1 !important;
      background: var(--v-checkbox-base);
      border-radius: 20px;
    }

    &::before {
      transition: border-color 0.24s ease, background-color 0.24s ease;
      width: 20px;
      height: 20px;
      border: 1px solid var(--v-gray-lighten2);
    }

    &::after {
      width: 10px;
      height: 10px;
      z-index: 10;
      left: 29px;
    }

    &:hover {
      &::before {
        border-color: var(--v-primary-base);
      }
    }

    &--active {
      &:before {
        border-color: var(--v-primary-base);
        background-color: var(--v-primary-base);
      }
    }
  }
}

.error-box {
  background-color: var(--v-error-lighten5);
  border-radius: 8px;
  padding: 16px;
  display: flex;
}

.warning-box {
  background-color: var(--v-warning-lighten5);
  border-radius: 8px;
  padding: 16px;
  display: flex;
}

@media (max-width: 480px) {
  .v-card__text,
  .v-card__actions {
    padding-left: 24px !important;
    padding-right: 24px !important;
  }
}
</style>
